import { config } from './config';
import { reportError } from './errorReport';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

async function request<T>(path: string, init: { method?: string; body?: unknown; auth?: boolean; headers?: Record<string, string> } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers || {}) };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (init.auth !== false) {
    const { data } = await supabase().auth.getSession();
    if (data.session) headers.Authorization = `Bearer ${data.session.access_token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { method: init.method || 'GET', headers, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
  } catch {
    throw new ApiError('Không kết nối được máy chủ API. Kiểm tra mạng rồi thử lại.', 0);   // mất mạng: không báo lỗi
  }
  const json = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) {
    if (res.status >= 500) reportError('api', `${init.method || 'GET'} ${path} → ${res.status} ${json.error ?? ''}`.trim());   // lỗi máy chủ mới báo; lỗi 4xx là do người dùng
    throw new ApiError(json.error || `Lỗi ${res.status}`, res.status);
  }
  return json as T;
}

export interface NewUserBody { username: string; fullName: string; email: string; phone?: string; title?: string; role: 'admin' | 'user'; modules: string[] }
export interface AttachmentDto { id: string; owner_type: 'employee' | 'document'; owner_id: string; kind: string; name: string; ext: string; size: number; storage_key: string }

export const api = {
  setup: (b: { fullName: string; username: string; email: string; password: string }, setupToken?: string) =>
    request<{ id: string }>('/setup', { method: 'POST', body: b, auth: false, headers: setupToken ? { 'x-setup-token': setupToken } : undefined }),
  createUser: (b: NewUserBody) => request<{ id: string; username: string; tempPassword: string }>('/admin/users', { method: 'POST', body: b }),
  resetPassword: (id: string) => request<{ tempPassword: string }>(`/admin/users/${id}/reset-password`, { method: 'POST' }),
  deleteUser: (id: string) => request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),

  /** Tải một tệp lên kho: xin link tạm → PUT thẳng lên kho → xác nhận để ghi siêu dữ liệu. */
  async uploadFile(file: File, owner: { type: 'employee' | 'document'; id: string }, kind: string): Promise<AttachmentDto> {
    const slot = await request<{ id: string; key: string; url: string; headers: Record<string, string> }>('/files/upload-url', {
      method: 'POST', body: { ownerType: owner.type, ownerId: owner.id, name: file.name, size: file.size, kind },
    });
    const put = await fetch(slot.url, { method: 'PUT', headers: slot.headers, body: file });
    if (!put.ok) throw new ApiError(`${file.name}: không tải lên được kho tệp (${put.status}).`, put.status);
    return request<AttachmentDto>('/files/confirm', { method: 'POST', body: { ownerType: owner.type, ownerId: owner.id, id: slot.id, key: slot.key, name: file.name, kind } });
  },
  fileUrl: (id: string, disposition: 'inline' | 'attachment' = 'inline') => request<{ url: string; name: string }>(`/files/${id}/url?disposition=${disposition}`),
  deleteFile: (id: string) => request<{ ok: true }>(`/files/${id}`, { method: 'DELETE' }),
};
